<template>
    <div>
        <section class="tabs-section">
            <NavBtns @navigatetotab="navigateToTab" :combo-ids="comboIds" :tabs="tabs" />
            <FlowNavigation currentStep="Checkout" />
        </section>

        <section class="inner-content-section py-9">
            <div class="checkout-container container">
                <!-- Combo package minimum selection warning -->
                <div v-if="showComboWarning" class="flex items-start gap-3 bg-[var(--light-primary,#fff8ee)] border border-[var(--primary)] rounded-lg px-4 py-3 text-sm text-[var(--charcoal-dark)] mb-4">
                    <svg class="shrink-0 mt-0.5" width="18" height="18" viewBox="0 0 24 24" fill="none">
                        <circle cx="12" cy="12" r="10" stroke="currentColor" stroke-width="2" fill="none" opacity="0.3"/>
                        <path d="M12 8v4m0 4h.01" stroke="currentColor" stroke-width="2" stroke-linecap="round"/>
                    </svg>
                    <span>This booking requires at least <strong>2 packages</strong>. Please go back and select at least one more package before proceeding.</span>
                </div>

                <div class="flex flex-wrap -mx-3">
                    <div class="checkout-right w-full lg:w-1/3 max-w-full px-3 lg:order-2">
                        <ItemizedList :items="cartItem" :global-total-item="globalTotal"
                            :seat-errors="seatErrors" @update-items="updateItems" @edit-item="editItem" />
                        <ItemTotalSummary :global-total="globalTotal" />
                    </div>
                    <div class="checkout-left w-full lg:w-2/3 max-w-full px-3 lg:order-1">
                        <CheckoutForm @onsubmit="submit" :items="cartItem" :tenant-id="tenantId" :errors="errors"
                            :global-total="globalTotal" @update-seat-errors="handleSeatErrorsUpdate" />
                    </div>
                </div>
            </div>
        </section>
    </div>
</template>

<script>
import axios from 'axios';
import { loadStripe } from '@stripe/stripe-js';
import { CountryCodes } from '../utils/geoUtils';
import { roundout } from '../utils/currency';
import NavBtns from './Nav/NavBtns.vue';
import FlowNavigation from './Nav/FlowNavigation.vue';
import CheckoutForm from './Checkout/CheckoutForm.vue';
import ItemizedList from './Checkout/ItemizedList.vue';
import ItemTotalSummary from './Checkout/ItemTotalSummary.vue';

export default {
    name: 'Checkout',
    title: 'Native American Tours',
    components: {
        NavBtns,
        FlowNavigation,
        CheckoutForm,
        ItemizedList,
        ItemTotalSummary,
    },
    props: {
        // Add props if passed from parent, none currently inferred
    },
    data() {
        return {
            processing: false,
            baseUrl: process.env.VUE_APP_BASE_URL,
            iframeStatus: true,
            tenantId: null,
            comboIds: 0,
            bookingIds: {},
            details: [],
            hotels: [],
            cartItem: {},
            cartItemLength: 0,
            errors: [],
            seatErrors: [],
            globalTotal: {
                subtotal: 0,
                discount: 0,
                fees: 0,
                addons_total: 0,
                addons_fee: 0,
                total: 0
            },
            message: '',
            stripe: null,
            elements: null,
            cardElement: null,
            stripeValidationError: '',
            name: null,
            phone_number: null,
            phone_code: '+1',
            email: null,
            getemailupdates: 0,
            cancellations_policy: 0,
            comment: null,
            paymentIntentId: null,
            stripeCustomerId: null,
            customTermAcknowledgments: {}
        };
    },
    computed: {
        tabs() {
            const hasCustomFields = this.$store.state.hasCustomFields;
            const isCombo = this.comboIds && this.comboIds !== 0;

            if (isCombo) {
                // Combo WITH add-ons: Browse(1), Init(2), Addons(3), MyTrip(4), Maps(5), Checkout(6)
                // Combo WITHOUT add-ons: Browse(1), Init(2), MyTrip(3), Maps(4), Checkout(5)
                // If hasCustomFields is null (unknown), default to 6 (assume add-ons exist)
                return hasCustomFields === false ? 5 : 6;
            } else {
                // Single WITH add-ons: Browse(1), Init(2), Addons(3), Checkout(4)
                // Single WITHOUT add-ons: Browse(1), Init(2), Checkout(3)
                // If hasCustomFields is null (unknown), default to 4 (assume add-ons exist)
                return hasCustomFields === false ? 3 : 4;
            }
        },
        showComboWarning() {
            return this.comboIds && this.cartItemLength < 2;
        }
    },
    async mounted() {
        this.stripe = await loadStripe(process.env.VUE_APP_STRIPE_PUBLISHABLE_KEY);
    },
    created() {
        this.initializeFromStore();
        this.calculateGlobalTotal();
        this.$store.dispatch('storeTabs', this.tabs);
        this.$store.dispatch('storeMindChange', 0);
    },
    methods: {
        initializeFromStore() {
            this.iframeStatus = this.$store.state.iframeStatus;
            this.tenantId = this.$store.state.tenantId;
            this.hotels = this.$store.state.tourPackage?.hotels || [];
            this.comboIds = this.$store.state.comboIds;
            this.cartItem = this.$store.state.cartItem || {};
            this.cartItemLength = Object.keys(this.cartItem).length;
        },
        calculateGlobalTotal() {
            if (!this.cartItemLength) return;

            const totals = Object.values(this.cartItem).reduce(
                (totals, item) => ({
                    subtotal: totals.subtotal + Number(item.subtotal),
                    discount: totals.discount + Number(item.discount2_value),
                    fees: totals.fees + Number(item.fees),
                    addons_total: totals.addons_total + Number(item.addons_total),
                    addons_fee: totals.addons_fee + Number(item.addons_fee),
                    total: totals.total + Number(item.total)
                }),
                {
                    subtotal: 0,
                    discount: 0,
                    fees: 0,
                    addons_total: 0,
                    addons_fee: 0,
                    total: 0
                }
            );

            this.globalTotal = Object.fromEntries(
                Object.entries(totals).map(([key, value]) => [key, roundout(value)])
            );

            Object.values(this.cartItem).forEach(item => {
                item.couponErrors = item.couponErrors || [];
                item.couponSuccess = item.couponSuccess || [];
            });
        },
        updateItems(updatedItems) {
            this.cartItem = updatedItems;
            this.cartItemLength = Object.keys(updatedItems).length;
            this.calculateGlobalTotal();
        },
        async submit(props) {
            if (this.processing) return;

            this.processing = true;
            const loader = this.$loading.show();
            this.errors = [];

            // Map props to data
            Object.assign(this, props);

            // Validate PI presence
            if (!this.paymentIntentId) {
                this.errors.push('Please enter your card information.');
            }

            if (this.errors.length) {
                this.processing = false;
                loader.hide();
                return;
            }

            try {
                this.cartItem = this.$store.state.cartItem || {};
                const phone_number = CountryCodes.formatPhoneNumber(this.phone_code, this.phone_number);
                const payload = {
                    items: this.cartItem,
                    combo_ids: this.comboIds,
                    name: this.name,
                    email: this.email,
                    phone_number,
                    comment: this.comment,
                    getemailupdates: this.getemailupdates,
                    cancellations_policy: this.cancellations_policy,
                    payment_intent_id: this.paymentIntentId,
                    stripe_customer_id: this.stripeCustomerId,
                    custom_term_acknowledgments: this.customTermAcknowledgments || {},
                    ...this.globalTotal
                };

                const response = await axios.post('/package-booking-tour', payload);
                this.$store.dispatch('storeCustomer', payload);

                if (response.data.success === 'false') {
                    this.errors.push(response.data.message || 'Booking could not be completed. Your payment was not charged.');
                    this.processing = false;
                    loader.hide();
                    return;
                }

                this.bookingIds = response.data.bookingIds;
                this.$store.dispatch('storeBookingIds', this.bookingIds);

                if (response.data.clientSecret) {
                    await this.handleStripePayment(response.data, payload);
                } else {
                    await axios.post('/delete-pre-booking', payload);
                    this.$router.push('/thankyou');
                }
            } catch (error) {
                this.handleError(error);
            } finally {
                this.processLoader(loader);
            }
        },
        async handleStripePayment(data, payload) {
            const loader = this.$loading.show();
            try {
                await this.stripe.confirmCardPayment(data.clientSecret);
                const stripeObject = {
                    booking_ids: data.bookingIds,
                    affiliate_id: data.affiliateId,
                    payment_intent: data.intentId,
                    payment_intent_client_secret: data.clientSecret
                };
                await axios.post('/package-booking-3ds-payment', stripeObject);
                this.processLoader(loader);
                await axios.post('/delete-pre-booking', payload);
                this.$router.push('/thankyou');
            } catch (error) {
                this.handleError(error);
                this.processLoader(loader);
            }
        },
        handleError(error) {
            if (error.response) {
                this.errors.push(error.response.data.message);
            } else {
                console.error('Error:', error.message);
            }
        },
        navigateToTab(tab, destination) {
            if ([1, 2, 3, 4, 5].includes(tab)) {
                if (tab === 2) {
                    this.$store.dispatch('storeFormData', null);
                }
                this.$store.dispatch('storeMindChange', 1);
                this.$router.push({ name: destination });
            }
        },
        processLoader(loader) {
            this.processing = false;
            loader.hide();
        },
        handleSeatErrorsUpdate(errors) {
            this.seatErrors = errors;
        },
        editItem(item) {
            this.$router.push({ name: 'Init' });
        }
    }
};
</script>

<style scoped>
.card-element {
    margin-top: 5px;
}

.card-label-text-left {
    text-align: left;
}

.error {
    color: #dc3545;
}
</style>